import React, { useState, useEffect } from "react";
import {
	Card,
	CardBody,
	CardHeader,
	CardTitle,
	Nav,
	NavItem,
	NavLink,
	TabContent,
	TabPane,
	Button,
	Row,
	Col,
	Container,
} from "reactstrap";
import { useHistory } from "react-router-dom";
import shallow from "zustand/shallow";
import useCurrentAccountStore from "../AccountStore";
import LeaseEndOptions from "./LeaseEndOptions";
import ExcessMileageFees from "./ExcessMileageFees";
import DamageAssessment from "./DamageAssessment";
import LeaseEndSummary from "./LeaseEndSummary";
import "./LeaseEndWizard.css";

const LeaseEndWizard = () => {
	const { account, lease } = useCurrentAccountStore((state) => state, shallow);
	const history = useHistory();

	// Wizard state
	const [activeStep, setActiveStep] = useState(1);
	const [leaseEndData, setLeaseEndData] = useState({
		selectedOption: null, // 'buyout', 'return', 'early_termination'
		buyoutAmount: 0,
		excessMileage: {
			currentMileage: 0,
			contractedMileage: 0,
			excessMiles: 0,
			ratePerMile: 0.25,
			totalMileageFee: 0,
		},
		damages: [],
		totalDamageFee: 0,
		totalFees: 0,
		paymentMethod: null,
	});

	// Calculate initial buyout amount
	// NOTE: Disposition fee handling:
	// - If capDispositionFee === 0 (At Lease End): Added to return amount only, NOT buyout
	// - If capDispositionFee === 1 (Drive-Off): Already paid upfront, not in balance or buyout
	// - If capDispositionFee === 2 (Capitalized): Already included in account.amtFinanced/balance, not added separately
	useEffect(() => {
		if (account && lease) {
			const remainingPayments = account.amtFinanced - account.principle;
			const remainingInterest = account.financeCharge - account.interest;
			const remainingTax = account.tax - account.taxPaid;
			const lateFees = account.lateFeeDue || 0;
			const adjustments =
				(account.debitCreditTotal || 0) - (account.debitPaid || 0);

			// Buyout = Balance + Residual (disposition NOT added - either already in balance or not applicable)
			const buyoutTotal =
				remainingPayments +
				remainingInterest +
				remainingTax +
				lateFees +
				adjustments +
				(lease.residualValue || 0);

			setLeaseEndData((prev) => ({
				...prev,
				buyoutAmount: buyoutTotal,
			}));
		}
	}, [account, lease]);

	// Wizard steps configuration
	const steps = [
		{ id: 1, title: "Lease End Options", component: LeaseEndOptions },
		{ id: 2, title: "Mileage Assessment", component: ExcessMileageFees },
		{ id: 3, title: "Damage Assessment", component: DamageAssessment },
		{ id: 4, title: "Summary & Payment", component: LeaseEndSummary },
	];

	const handleNext = () => {
		if (activeStep === 1) {
			// From options step
			if (leaseEndData.selectedOption === "buyout") {
				// Buyout - skip mileage/damage, go directly to summary
				setActiveStep(4);
			} else {
				// Return or early termination - go to mileage assessment
				setActiveStep(2);
			}
		} else if (activeStep < steps.length) {
			setActiveStep(activeStep + 1);
		}
	};

	const handleBack = () => {
		if (activeStep === 4 && leaseEndData.selectedOption === "buyout") {
			// From summary back to options (skip mileage/damage for buyout)
			setActiveStep(1);
		} else if (
			activeStep === 2 &&
			(leaseEndData.selectedOption === "return" ||
				leaseEndData.selectedOption === "early_termination")
		) {
			// From mileage back to options
			setActiveStep(1);
		} else if (activeStep > 1) {
			setActiveStep(activeStep - 1);
		}
	};

	const handleCancel = () => {
		history.goBack();
	};

	const updateLeaseEndData = (data) => {
		setLeaseEndData((prev) => ({ ...prev, ...data }));
	};

	// Calculate total fees
	useEffect(() => {
		const totalFees =
			leaseEndData.excessMileage.totalMileageFee + leaseEndData.totalDamageFee;
		setLeaseEndData((prev) => ({ ...prev, totalFees }));
	}, [leaseEndData.excessMileage.totalMileageFee, leaseEndData.totalDamageFee]);

	const CurrentStepComponent = steps.find(
		(s) => s.id === activeStep
	)?.component;

	// Check if it's a lease or RTO account (payoffMethod: 4=Lease, 5=RTO)
	if (account.payoffMethod !== 4 && account.payoffMethod !== 5) {
		return (
			<Container className="mt-4">
				<Card>
					<CardBody>
						<h4>This feature is only available for Lease and RTO accounts.</h4>
						<Button color="primary" onClick={handleCancel}>
							Go Back
						</Button>
					</CardBody>
				</Card>
			</Container>
		);
	}

	return (
		<Container fluid>
			<Card>
				<CardHeader>
					{/* <CardTitle tag="h3">Select Lease End Option</CardTitle> */}
					{/* <p className="text-muted">
						Account: {account.accountNumber} | Customer:{" "}
						{account.buyerName || "N/A"}
					</p> */}
				</CardHeader>
				<CardBody>
					{/* Step Content */}
					<div className="wizard-content">
						{CurrentStepComponent && (
							<CurrentStepComponent
								account={account}
								lease={lease}
								leaseEndData={leaseEndData}
								updateLeaseEndData={updateLeaseEndData}
								onNext={handleNext}
								onBack={handleBack}
							/>
						)}
					</div>

					{/* Navigation Buttons */}
					<Row className="wizard-navigation mt-4">
						<Col>
							<Button
								color="secondary"
								onClick={handleBack}
								disabled={activeStep === 1}
							>
								Previous
							</Button>
						</Col>
						<Col className="text-center">
							<Button color="danger" onClick={handleCancel}>
								Cancel
							</Button>
						</Col>
						<Col className="text-right">
							{activeStep < steps.length && (
								<Button
									color="primary"
									onClick={handleNext}
									disabled={!leaseEndData.selectedOption && activeStep === 1}
								>
									Next
								</Button>
							)}
						</Col>
					</Row>
				</CardBody>
			</Card>
		</Container>
	);
};

export default LeaseEndWizard;
